import {
  matchField,
  notMatchField,
  type Validator,
  notConsecutiveCharacters,
  doesNotContain,
  minLength,
} from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  PasswordField,
  Stack,
} from 'braid-design-system';
import React, { type FormEventHandler } from 'react';

import type { ErrorMessage } from 'src/types';

import DontHavePasswordLink from '../../DontHavePassword/DontHavePasswordLinkContainer';
import ServerError from '../../ServerError/ServerError';

import translations from './.vocab';
import type { PasswordUseField } from './FormContainer';
import {
  consecutiveCharactersValidator,
  MINIMUM_PASSWORD_LENGTH,
} from './helpers';

export const FORM_NAME = 'changePassword';

interface Props {
  onSubmit: FormEventHandler;
  error?: ErrorMessage;
  toggle: () => void;
  useField: PasswordUseField;
  emailAddress: string;
  submitting: boolean;
}

const UserForm = ({
  onSubmit,
  error,
  toggle,
  useField,
  emailAddress,
  submitting,
}: Props) => {
  const { t } = useTranslations(translations);
  const consecutiveCharacters = consecutiveCharactersValidator(
    t('Cannot contain three identical characters in a sequence'),
  );

  const currentPassword = useField({ id: 'currentPassword' });
  const name = emailAddress ? emailAddress.split('@')[0] : '';
  const newPassword = useField({
    id: 'newPassword',
    validators: [
      minLength,
      notMatchField as Validator<
        string,
        {
          notMatchFieldName: string;
          notMatchErrorMessage: string;
        },
        Record<string, any>,
        string
      >,
      consecutiveCharacters,
      doesNotContain,
      notConsecutiveCharacters,
    ],
    validatorProps: {
      min: MINIMUM_PASSWORD_LENGTH,
      consecutive: 2,
      notMatchFieldName: 'currentPassword',
      notMatchErrorMessage: t('Cannot be your current password'),
      emailAddress,
      forbiddenStrings: [name],
      doesNotContainMessage: t(
        'Cannot contain your name or part of your email',
      ),
    },
  });
  const confirmPassword = useField({
    id: 'confirmPassword',
    validators: [
      matchField as Validator<
        string,
        { matchFieldName: string; matchErrorMessage: string },
        Record<string, any>,
        string
      >,
    ],
    validatorProps: {
      matchFieldName: 'newPassword',
      matchErrorMessage: t('The password does not match'),
    },
  });
  return (
    <form onSubmit={onSubmit}>
      <Stack space="large">
        <Stack space="large">
          <Stack space="medium">
            <Box paddingBottom="medium">
              <Stack space="small">
                <PasswordField
                  id="currentPassword"
                  name="currentPassword"
                  label={t('Current password')}
                  {...currentPassword}
                />
                <DontHavePasswordLink originator="change password" />
              </Stack>
            </Box>
            <PasswordField
              id="newPassword"
              name="newPassword"
              label={t('Enter new password')}
              reserveMessageSpace
              {...newPassword}
            />
            <PasswordField
              id="confirmPassword"
              name="confirmPassword"
              label={t('Confirm password')}
              reserveMessageSpace
              {...confirmPassword}
            />
          </Stack>
          {error && !submitting ? <ServerError error={error} /> : null}
        </Stack>

        <Actions>
          <Button type="submit" tone="formAccent" loading={submitting}>
            {t('Save')}
          </Button>
          <Button variant="transparent" tone="formAccent" onClick={toggle}>
            {t('Cancel')}
          </Button>
        </Actions>
      </Stack>
    </form>
  );
};

export default UserForm;
